import React from 'react'
import styles from './BottomBar.scss'
import { SingleLineEditor } from 'common/SingleLineEditor'
import { Button } from 'common/Button'
import { Icon } from 'common/LegacyIcon'
import { CircleSpinner } from 'common/spinners'
import smartDate from '@qrohlf/smart-date'
import fetchJSON from 'lib/fetchJSON'
import { photoFull } from 'lib/urlHelpers'
import { Msg, commonNouns, commonVerbs } from 'lib/localization'
import cx from 'classnames'
import { defineMessages } from 'react-intl'
const preventDefault = e => e.preventDefault()

const bbMessages = defineMessages({
  addCaption: {
    id: 'bbMessages.addCaption',
    defaultMessage: 'Click to add a caption'
  },
  confirm: {
    id: 'bbMessages.confirm',
    defaultMessage: 'Are you sure you want to delete this photo?'
  },
  cover: {
    id: 'bbMessages.cover',
    defaultMessage: 'cover image',
    description: 'The image displayed first for an asset'
  },
  downloadOriginal: {
    id: 'bbMessages.downloadOriginal',
    defaultMessage: 'download original',
    description: 'A link that initiates a download of the original image file uploaded to our site'
  },
  goTo: {
    id: 'bbMessages.goTo',
    defaultMessage: 'go to {asset}'
  },
  setAsCover: {
    id: 'bbMessages.setAsCover',
    defaultMessage: 'set as cover',
    description: 'Set this image as the primary image for an asset'
  }
})

export class BottomBar extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      captionHasFocus: false
    }
  }

  onCaptionFocus = () => {
    this.setState({ captionHasFocus: true })
  }

  onCaptionBlur = () => {
    this.setState({ captionHasFocus: false })
  }

  render () {
    const {
      readonly,
      photo,
      onCaptionChange,
      onDelete,
      rotating,
      onRotate,
      onRotateComplete,
      photosetId,
      parentType,
      parentId,
      coverId,
      onSetCover,
      intl
    } = this.props
    const { captionHasFocus } = this.state

    return (
      <div className={styles.BottomBar}>
        <div className={styles.left}>
          {photo.captured_at && smartDate(typeof photo.captured_at === 'string' ? photo.captured_at : photo.captured_at.toISOString())}
          {photo.parent_type === 'trip' && <a href={`/trips/${photo.parent_id}`} className={styles.tripLink}>
          (<Msg m={bbMessages.goTo} values={{ asset: intl.formatMessage(commonNouns.rides, { count: 1 }) }} />)
          </a>}
          {photo.parent_type === 'route' && <a href={`/routes/${photo.parent_id}`} className={styles.tripLink}>
          (<Msg m={bbMessages.goTo} values={{ asset: intl.formatMessage(commonNouns.routes, { count: 1 }) }} />)
          </a>}
        </div>
        {readonly
          ? (photo.caption && <p className={styles.caption}>{photo.caption}</p>)
          : <CaptionEditor
            intl={intl}
            photo={photo}
            key={`${photo.id}-caption`}
            onRotate={onRotate}
            onFocus={this.onCaptionFocus}
            onBlur={this.onCaptionBlur}
            hasFocus={captionHasFocus}
            onRotateComplete={onRotateComplete}
            onCaptionChange={onCaptionChange}
            rotating={rotating} />}
        <div className={styles.right}>
          {!readonly && <Toolbox
            intl={intl}
            photo={photo}
            onRotate={onRotate}
            onRotateComplete={onRotateComplete}
            onDelete={onDelete}
            photosetId={photosetId}
            parentType={parentType}
            parentId={parentId}
            coverId={coverId}
            captionHasFocus={captionHasFocus}
            onSetCover={onSetCover} />}
        </div>
      </div>
    )
  }
}

class Toolbox extends React.Component {
  onRotateLeft = () => {
    this.rotate(-90)
  }

  onRotateRight = () => {
    this.rotate(90)
  }

  rotate = (degrees) => {
    const { photo } = this.props
    this.props.onRotate()
    fetchJSON(`/photos/${photo.id}/rotate.json`, { degrees })
      .then(() => {
        this.props.onRotateComplete()
      })
  }

  onDelete = () => {
    if (window.confirm(this.props.intl.formatMessage(bbMessages.confirm))) {
      this.props.onDelete()
    }
  }

  onSetHighlight = (photoId) => {
    const { photo, onSetCover } = this.props
    onSetCover(photo.id)
    fetchJSON(`/photos/${photoId}/highlight.json`, {}, { method: 'POST' })
  }

  render () {
    const { photo, coverId, photosetId, captionHasFocus, intl } = this.props
    const photosetButton = photo.id === coverId
      ? <span className={styles.coverLink}><Msg m={bbMessages.cover} /></span>
      : <a onClick={() => this.onSetHighlight(photo.id)} className={styles.coverLink}><Msg m={bbMessages.setAsCover} /></a>
    return (
      <div className={styles.toolbox}>
        {photosetId && !captionHasFocus && photosetButton}
        <Icon i='rotateLeft' size={18} onClick={this.onRotateLeft} />
        <Icon i='rotateRight' size={18} onClick={this.onRotateRight} />
        <a href={photoFull(photo.id)} alt='Download original' download={`${photo.id}.jpg`} title={intl.formatMessage(bbMessages.downloadOriginal)}><Icon i='download' size={18} /></a>
        <Icon i='trash' size={18} onClick={this.onDelete} />
      </div>
    )
  }
}

class CaptionEditor extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      caption: props.caption,
      saving: false
    }
  }

  onChange = (caption) => {
    this.setState({ caption })
  }

  saveCaption = () => {
    const { photo, onCaptionChange } = this.props
    const { caption } = this.state

    onCaptionChange(caption)

    this.setState({ saving: true })
    fetchJSON('/photos/update_set.json', {
      photos: {
        [photo.id]: { caption }
      }
    }).then(() => {
      // make sure we show spinner for at least 1 second
      setTimeout(() => {
        this.setState({ saving: false })
        this._editor && this._editor.blur()
      }, 1000)
    })
  }

  setRef = (editor) => {
    this._editor = editor
  }

  render () {
    const { saving } = this.state
    const { photo, hasFocus, intl } = this.props

    return (
      <div className={cx(styles.caption, styles.captionEditor, hasFocus && styles.hasFocus)}>
        <SingleLineEditor
          ref={this.setRef}
          onFocus={this.props.onFocus}
          onBlur={this.props.onBlur}
          defaultValue={photo.caption}
          placeholder={intl.formatMessage(bbMessages.addCaption)}
          onChange={this.onChange}
          onReturn={this.saveCaption}
          textAlignment='center' />
        {hasFocus && !saving && <Button onClick={this.saveCaption} className={styles.saveButton} onMouseDown={preventDefault}>
          <Msg lowerCase m={commonVerbs.save} />
        </Button>}
        {saving && <CircleSpinner size={14} className={styles.saveSpinner} />}
      </div>
    )
  }
}
